import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { useFinancialWarnings } from 'contexts/FinancialWarningsContext';
import { debounce } from 'lodash';
import { useGetCashToSupplierCalculation } from 'services/calculations/cashFlow';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import { isOpeningBalanceAssetsEmpty } from 'components/BusinessToolForm/utils/financialCalculations';
import {
  PERIODS_OPERATION_TYPES,
  subtractPeriodValueBaseOnPreviousPeriod,
  summarizeSubtractPeriodValues,
} from 'components/BusinessToolForm/utils/helpers';

import ToastService from 'helpers/ToastService';

import { createCashToSuppliersParams } from './helper';
import { IFormContentTypes, observedFields } from '../../utils/form';

export const useCashToSuppliers = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getCashToSupplierCalculation = useGetCashToSupplierCalculation();
  const { setIntangibleAmortizationData, setPpeDepreciationData } = useFinancialWarnings();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [
    cashPaidToSuppliersFiled,
    inventoryField,
    accountsPayableField,
    deprecationAmortizationField,
    changeInInventoryField,
    changeInAccountsPayableField,
    deprecationField,
    amortizationField,
  ] = observedFields.cashFlowToSuppliers.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (content: IFormContentTypes['cashFlowToSuppliers']) => {
        const { cashToSuppliersParams, unit } = createCashToSuppliersParams(content);
        const [, , , , , , , , , , , , propertyPlantAndEquipment, intangibleAssets] = content;
        const openingBalanceIsEmpty = isOpeningBalanceAssetsEmpty(propertyPlantAndEquipment, intangibleAssets);

        const params = {
          unit,
          businessPlanId,
          cashToSuppliersParams,
        };

        try {
          const { cashPaidToSupplier, inventory, accountsPayable, amortization, depreciationFixedExpense } =
            await getCashToSupplierCalculation.mutateAsync(params);

          setIntangibleAmortizationData(amortization as unknown as IPeriodsTableRow);
          setPpeDepreciationData(depreciationFixedExpense as unknown as IPeriodsTableRow);

          const changeInInventory = subtractPeriodValueBaseOnPreviousPeriod(
            inventory,
            String(cashToSuppliersParams.inventory),
          );
          const changeInAccountsPayable = subtractPeriodValueBaseOnPreviousPeriod(
            accountsPayable,
            String(cashToSuppliersParams.initAccountPayable),
          );

          const zeroedDepreciation = openingBalanceIsEmpty ? initialPeriodsTableRow : depreciationFixedExpense;
          const zeroedAmortization = openingBalanceIsEmpty ? initialPeriodsTableRow : amortization;
          const deprecationAmortizationFixedExpense = openingBalanceIsEmpty
            ? initialPeriodsTableRow
            : summarizeSubtractPeriodValues([amortization, depreciationFixedExpense], PERIODS_OPERATION_TYPES.ADD);

          setValue(cashPaidToSuppliersFiled, cashPaidToSupplier);
          setValue(inventoryField, inventory);
          setValue(accountsPayableField, accountsPayable);
          setValue(deprecationAmortizationField, deprecationAmortizationFixedExpense);
          setValue(changeInInventoryField, changeInInventory);
          setValue(changeInAccountsPayableField, changeInAccountsPayable);
          setValue(deprecationField, zeroedDepreciation);
          setValue(amortizationField, zeroedAmortization);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 800),

    [],
  );

  const cashToSuppliersCalculationHandler = useCallback(
    async (content: IFormContentTypes['cashFlowToSuppliers']) => {
      if (!canAccess) return;

      debouncedUpdateFunction(content);
    },

    [debouncedUpdateFunction],
  );

  return cashToSuppliersCalculationHandler;
};
